export interface SubNavLink {
  label: string;
  path: string;
}

export interface SubNavActionButton {
  label: string;
  path: string;
}

export interface SubNavConfig {
  title: string;
  showUiToggle: boolean;
  actionButton?: SubNavActionButton;
  links: SubNavLink[];
  /** If true, show case-wide navigation tabs above this subnav */
  showCaseTabs?: boolean;
}

/** Case-wide navigation tabs that appear on all case pages */
export function getCaseWideTabs(caseId: string): SubNavLink[] {
  return [
    { label: 'Overview', path: `/cases/${caseId}` },
    { label: 'Journal', path: `/cases/${caseId}/journal` },
    { label: 'Evidence', path: `/cases/${caseId}/exhibits` },
    { label: 'Timeline', path: `/cases/${caseId}/timeline` },
    { label: 'Forms & Filings', path: `/cases/${caseId}/filings` },
    { label: 'Drafts', path: `/cases/${caseId}/drafts` },
  ];
}

export const SUB_NAV_CONFIG: Record<string, SubNavConfig> = {
  '/dashboard': {
    title: 'Case Overview',
    showUiToggle: true,
    links: [],
  },
  '/cases/:id': {
    title: 'Case Overview',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/journal': {
    title: 'Journal',
    showUiToggle: true,
    showCaseTabs: true,
    actionButton: { label: '+ New Entry', path: '/cases/:id/journal' },
    links: [
      { label: 'All Entries', path: '/cases/:id/journal' },
      { label: 'Search', path: '/cases/:id/journal/search' },
    ],
  },
  '/cases/:id/timeline': {
    title: 'Timeline',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/exhibits': {
    title: 'Evidence',
    showUiToggle: true,
    showCaseTabs: true,
    links: [
      { label: 'All Evidence Sets', path: '/cases/:id/exhibits' },
      { label: 'Bates Stamping', path: '/cases/:id/exhibits/bates' },
      { label: 'Manage Tags', path: '/cases/:id/exhibits/tags' },
    ],
  },
  '/cases/:id/filings': {
    title: 'Forms & Filings',
    showUiToggle: true,
    showCaseTabs: true,
    links: [
      { label: 'All Filings', path: '/cases/:id/filings' },
      { label: 'Filings', path: '/cases/:id/filings' },
    ],
  },
  '/cases/:id/forms': {
    title: 'Forms',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/calendar': {
    title: 'Calendar',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/drafts': {
    title: 'Drafts',
    showUiToggle: true,
    showCaseTabs: true,
    actionButton: { label: '+ New Draft', path: '/cases/:id/drafts/new' },
    links: [],
  },
  '/cases/:id/insights': {
    title: 'Insights',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/inbox': {
    title: 'Evidence Intake',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/notifications': {
    title: 'Communications',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/share': {
    title: 'Share',
    showUiToggle: false,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/exports': {
    title: 'Exports',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/toolkits': {
    title: 'Toolkits',
    showUiToggle: true,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/scan': {
    title: 'Scan',
    showUiToggle: false,
    showCaseTabs: true,
    links: [],
  },
  '/cases/:id/attachments': {
    title: 'Attachments',
    showUiToggle: false,
    showCaseTabs: true,
    links: [],
  },
  '/profile': {
    title: 'Profile & Settings',
    showUiToggle: false,
    links: [],
  },
  '/billing': {
    title: 'Billing & Subscription',
    showUiToggle: false,
    links: [],
  },
  '/help': {
    title: 'Help Center',
    showUiToggle: false,
    links: [],
  },
  '/search': {
    title: 'Search',
    showUiToggle: false,
    links: [],
  },
};

/**
 * Get sub-navigation configuration for a given route path
 * Handles dynamic route parameters like :id
 * 
 * NOTE (v1.7.1 hotfix): SecondarySubNav disabled for all case routes.
 * PrimarySidebar provides case navigation. Duplicate horizontal nav removed.
 */
export function getSubNavConfig(pathname: string, caseId?: string): SubNavConfig | null {
  // HOTFIX v1.7.1: Disable SecondarySubNav for all case routes
  // PrimarySidebar provides complete case navigation (7 links in sidebar)
  // This eliminates duplicate horizontal case navigation that confused users
  if (pathname.startsWith('/cases/') && caseId) {
    return null;
  }

  // Direct match first
  if (SUB_NAV_CONFIG[pathname]) {
    return SUB_NAV_CONFIG[pathname];
  }

  // Try to match with :id replacement
  if (caseId) {
    for (const [pattern, config] of Object.entries(SUB_NAV_CONFIG)) {
      const regex = new RegExp(`^${pattern.replace(':id', '[^/]+')}$`);
      if (regex.test(pathname)) {
        // Replace :id in paths with actual caseId
        const processedConfig: SubNavConfig = {
          ...config,
          links: config.links.map(link => ({
            ...link,
            path: link.path.replace(':id', caseId),
          })),
        };
        if (config.actionButton) {
          processedConfig.actionButton = {
            ...config.actionButton,
            path: config.actionButton.path.replace(':id', caseId),
          };
        }
        return processedConfig;
      }
    }
  }

  return null;
}
